/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `RequirementListCard` for the Caintra Connect frontend.
 * @module components/Requirements/RequirementListCard
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import dayjs from "dayjs";
import { Switch } from "@mui/material";
import { ReactComponent as ClockIcon } from "../../assets/icons/ViewRegistrationModal/ClockIcon.svg";
import { ReactComponent as ActiveRequirementIcon } from "../../assets/icons/ProposalsListModal/ActiveRequirementIcon.svg";
import { getRequirementSectorImageUrl } from "../../utils/requirements/requirementDisplay.js";
import AssetImage from "../AssetImage";

const RequirementListCard = ({
  requirement,
  index,
  tabIndex,
  isDesktop,
  currentUser,
  t,
  styles,
  onViewRequirement,
  onViewProposals,
  onRejectRequirement,
  onApproveRequirement,
  onToggleActive,
}) => (
  <div className={styles.requirementCard}>
    <div className={styles.requirementCardImage}>
      <AssetImage
        src={getRequirementSectorImageUrl(requirement)}
        alt={requirement.nombreCategoria}
      />
    </div>
    {!requirement.aprobado && (
      <div className={styles.pendingApprovalContainer}>
        <ClockIcon className={styles.icon} />
        <span className={styles.pendingApprovalText}>
          {t("pages.requirements.pendingApprovalText")}
        </span>
      </div>
    )}
    <div className={styles.requirementCardInfo}>
      <span className={styles.companyName}>{requirement.nombreEmpresa}</span>
      <div className={styles.titleContainer}>
        {tabIndex === 1 && !isDesktop && (
          <ActiveRequirementIcon
            style={{
              fill: requirement.activo ? "#00E864" : "#FF0000",
            }}
          />
        )}
        <span className={styles.requirementTitle}>{requirement.titulo}</span>
      </div>
      <div className={styles.requirementDetails}>
        <span className={styles.requirementDueDate}>
          {dayjs(requirement.fechaLimite).utc().format("DD/MM/YYYY")}
        </span>
        {tabIndex === 1 && isDesktop && (
          <>
            <span>•</span>
            <div className={styles.requirementStatus}>
              {requirement.activo ? (
                <>
                  <ActiveRequirementIcon style={{ fill: "#00E864" }} />
                  <span>{t("pages.requirements.activeRequirement")}</span>
                </>
              ) : (
                <>
                  <ActiveRequirementIcon style={{ fill: "#FF0000" }} />
                  <span>{t("pages.requirements.inactiveRequirement")}</span>
                </>
              )}
            </div>
          </>
        )}
      </div>
      <div className={styles.categoryTag}>
        <span>{requirement.nombreCategoria}</span>
      </div>
      <div
        className={
          tabIndex === 2
            ? styles.approveRejectButtonsContainer
            : styles.buttonsContainer
        }
      >
        <div
          className={styles.viewRequirementButton}
          onClick={() => onViewRequirement(requirement)}
        >
          {t("pages.requirements.viewRequirement")}
        </div>
        {tabIndex === 1 && (
          <div
            className={styles.viewProposalButton}
            onClick={() => onViewProposals(requirement)}
          >
            {t("pages.requirements.viewProposals")}
          </div>
        )}
        {tabIndex === 2 &&
          (currentUser?.esColaborador || currentUser?.tipoDeUsuario === "5") && (
            <div className={styles.approveRejectButtons}>
              <div
                className={styles.rejectButton}
                onClick={() => onRejectRequirement(requirement)}
              >
                {t("pages.requirements.reject")}
              </div>
              <div
                className={styles.approveButton}
                onClick={() => onApproveRequirement(requirement._id)}
              >
                {t("pages.requirements.approve")}
              </div>
            </div>
          )}
      </div>
      {tabIndex === 1 && (
        <div className={styles.requirementCardFooter}>
          <span className={styles.toggleActiveText}>
            {t("pages.requirements.activateRequirement")}
          </span>
          <Switch
            sx={{ fontSize: "1rem", zIndex: 0 }}
            checked={requirement.activo}
            onChange={() => onToggleActive(index)}
            color="primary"
          />
        </div>
      )}
    </div>
  </div>
);

export default RequirementListCard;
